import { c as _c } from "react/compiler-runtime";
import { cloneElement, isValidElement, useId } from 'react';
import { useFormikContext } from 'formik';
import classNames from 'classnames';
import ChoiceGroup from '@helios/ChoiceGroup';
import FormGroup from '@helios/FormGroup';
import RegularXmarkIcon from '@/components/icons/regular/xmark';
import { SHOW_FIELD_ERROR_TEXT, shownFieldError } from './fieldErrorMessages';
import styles from './FieldGroup.module.scss';

/**
 * The app's Formik-aware field wrapper (Phase 5 PR 5.1): a label, one
 * `Field` and its error, on Helios FormGroup. Formik stays in app code.
 *
 * - The control takes an `id` (its own wins; else a generated one) and the
 *   label's `htmlFor` names it.
 * - `error`: the text the field shows today, passed under its own
 *   condition; it shows whatever `SHOW_FIELD_ERROR_TEXT` says. Otherwise,
 *   while the switch is on, a touched field's error shows the message from
 *   `fieldErrorMessages.js` (O164).
 * - No `required`: no marker or `aria-required` appears where none shows
 *   today.
 * - Without a `label` (a field named by a group legend, its placeholder or
 *   its own `aria-label`), FieldGroup renders the control and the error
 *   alone, in FormGroup's error look.
 */
function FieldGroup(t0) {
  const $ = _c(18);
  const {
    label,
    name,
    error,
    children
  } = t0;
  const child = isValidElement(children) ? children : null;
  const fieldName = name ?? child?.props.name;
  const meta = useFieldMeta(fieldName);
  const generatedId = useId();
  const id = child?.props.id ?? generatedId;
  let t1;
  if ($[0] !== error || $[1] !== fieldName || $[2] !== meta) {
    t1 = shownFieldError({
      name: fieldName,
      meta,
      legacyError: error,
      show: SHOW_FIELD_ERROR_TEXT
    });
    $[0] = error;
    $[1] = fieldName;
    $[2] = meta;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const shownError = t1;
  if (label === undefined) {
    let t2;
    if ($[4] !== child || $[5] !== children || $[6] !== id || $[7] !== shownError) {
      t2 = <UnlabelledField control={child} id={id} error={shownError}>{children}</UnlabelledField>;
      $[4] = child;
      $[5] = children;
      $[6] = id;
      $[7] = shownError;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  let t2;
  if ($[9] !== child || $[10] !== children || $[11] !== id) {
    t2 = child ? cloneElement(child, {
      id
    }) : children;
    $[9] = child;
    $[10] = children;
    $[11] = id;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[13] !== id || $[14] !== label || $[15] !== shownError || $[16] !== t2) {
    t3 = <FormGroup label={label} htmlFor={id} error={shownError}>{t2}</FormGroup>;
    $[13] = id;
    $[14] = label;
    $[15] = shownError;
    $[16] = t2;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
}

/**
 * The field's Formik meta (touched, error), read without registering the
 * field a second time: the `Field` inside registers it.
 */
function useFieldMeta(name) {
  const $ = _c(3);
  const formik = useFormikContext();
  let t0;
  if ($[0] !== formik || $[1] !== name) {
    t0 = name ? formik.getFieldMeta(name) : undefined;
    $[0] = formik;
    $[1] = name;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}

/**
 * A control with no label of its own, and its error drawn as FormGroup
 * draws one: the control gains `aria-invalid` and names the message in
 * `aria-describedby`.
 */
function UnlabelledField(t0) {
  const $ = _c(13);
  const {
    control,
    id,
    error,
    children
  } = t0;
  const errorId = useId();
  const hasError = Boolean(error);
  let t1;
  if ($[0] !== children || $[1] !== control || $[2] !== errorId || $[3] !== hasError || $[4] !== id) {
    t1 = control ? cloneElement(control, {
      id,
      ...(hasError && {
        "aria-describedby": [control.props["aria-describedby"], errorId].filter(Boolean).join(" "),
        "aria-invalid": true
      })
    }) : children;
    $[0] = children;
    $[1] = control;
    $[2] = errorId;
    $[3] = hasError;
    $[4] = id;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const field = t1;
  let t2;
  if ($[6] !== error || $[7] !== errorId || $[8] !== hasError) {
    t2 = hasError && <FieldError id={errorId}>{error}</FieldError>;
    $[6] = error;
    $[7] = errorId;
    $[8] = hasError;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== field || $[11] !== t2) {
    t3 = <div className={styles.field}>{field}{t2}</div>;
    $[10] = field;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}

/** An error message in FormGroup's look: a red cross and the message. */
export function FieldError(t0) {
  const $ = _c(6);
  const {
    id,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <RegularXmarkIcon className={styles.error__icon} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <span>{children}</span>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== id || $[4] !== t2) {
    t3 = <span id={id} className={styles.error} role="alert">{t1}{t2}</span>;
    $[3] = id;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

/**
 * A radio or checkbox question: Helios ChoiceGroup, its legend holding the
 * question's existing nodes (an `h4` stays an `h4`), with the error rule of
 * FieldGroup read from the group's `name`.
 */
export function FieldChoiceGroup(t0) {
  const $ = _c(11);
  const {
    legend,
    name,
    error,
    appearance: t1,
    columns,
    legendHidden,
    children
  } = t0;
  const appearance = t1 === undefined ? "card" : t1;
  const meta = useFieldMeta(name);
  let t2;
  if ($[0] !== error || $[1] !== meta || $[2] !== name) {
    t2 = shownFieldError({
      name,
      meta,
      legacyError: error,
      show: SHOW_FIELD_ERROR_TEXT
    });
    $[0] = error;
    $[1] = meta;
    $[2] = name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const shownError = t2;
  let t3;
  if ($[4] !== appearance || $[5] !== children || $[6] !== columns || $[7] !== legend || $[8] !== legendHidden || $[9] !== shownError) {
    t3 = <ChoiceGroup legend={legend} legendHidden={legendHidden} appearance={appearance} columns={columns} error={shownError}>{children}</ChoiceGroup>;
    $[4] = appearance;
    $[5] = children;
    $[6] = columns;
    $[7] = legend;
    $[8] = legendHidden;
    $[9] = shownError;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}

/**
 * Two (or more) controls named by one label, such as "When were you there?"
 * over From and To: a fieldset whose legend is the label's text in the
 * Helios label type.
 */
export function FieldSet(t0) {
  const $ = _c(5);
  const {
    legend,
    children
  } = t0;
  let t1;
  if ($[0] !== legend) {
    t1 = <legend className={styles.legend}>{legend}</legend>;
    $[0] = legend;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <fieldset className={styles.fieldset}>{t1}{children}</fieldset>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/**
 * Fields that share a row: one column below 576px, then the given split
 * (`even`, or `wide-narrow` for 7 / 5). A column gap only: each FormGroup's
 * own 20px bottom margin is the form's rhythm, which Helios Grid's single
 * gap would double when the row stacks.
 */
export function FieldRow(t0) {
  const $ = _c(5);
  const {
    split: t1,
    children
  } = t0;
  const split = t1 === undefined ? "even" : t1;
  const t2 = styles[`field-row--${split}`];
  let t3;
  if ($[0] !== t2) {
    t3 = classNames(styles["field-row"], t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children || $[3] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[2] = children;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}

/** The muted hint inside a label, such as "(optional)". */
export function FieldHint(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span className={styles.hint}>{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export default FieldGroup;